/**
 * ShapeLayer Component
 *
 * Renders simple geometric shapes (rect, circle, line) within an SVG block.
 * Useful for visual accents, dividers, and background elements.
 */

import { useId } from 'react';
import type { ShapeLayer as ShapeLayerType } from '@bendyline/squisq/schemas';
import { getAnimationStyle } from '../utils/animationUtils';
import { resolveValue, getAnchorOffset } from '../utils/layerUtils';
import { resolveFill, resolveShapeFilter, borderDashArray } from '../utils/fillStyle';

interface ShapeLayerProps {
  layer: ShapeLayerType;
  /** Viewport dimensions for percentage calculations */
  viewport: { width: number; height: number };
  /** Current time relative to block start */
  blockTime: number;
}

/**
 * Full-bleed HTML and SVG layers can be rasterized on slightly different
 * subpixel boundaries. Extend unbordered background rectangles beyond the
 * viewport so an image cannot peek through at the outermost pixel; the block
 * renderer clips the combined layer stack back to the viewBox.
 */
const FULL_BLEED_OVERSCAN = 1;

export function ShapeLayer({ layer, viewport, blockTime }: ShapeLayerProps) {
  const { content, position, animation } = layer;
  const defsId = `${useId().replace(/:/g, '')}-${layer.id}`;

  // Resolve position values to pixels
  const rawX = resolveValue(position.x, viewport.width);
  const rawY = resolveValue(position.y, viewport.height);
  const width = position.width ? resolveValue(position.width, viewport.width) : 100;
  const height = position.height ? resolveValue(position.height, viewport.height) : 100;

  // Apply anchor offset (e.g., 'center' shifts x by -width/2 and y by -height/2)
  const anchorOffset = getAnchorOffset(position.anchor, width, height);
  const x = rawX + anchorOffset.x;
  const y = rawY + anchorOffset.y;

  const isUnborderedFullBleedRect =
    content.shape === 'rect' &&
    x === 0 &&
    y === 0 &&
    width === viewport.width &&
    height === viewport.height &&
    !content.stroke &&
    !content.borderRadius;
  const overscan = isUnborderedFullBleedRect ? FULL_BLEED_OVERSCAN : 0;
  const paintX = x - overscan;
  const paintY = y - overscan;
  const paintWidth = width + overscan * 2;
  const paintHeight = height + overscan * 2;

  // Get animation styles
  const animStyle = getAnimationStyle(animation, blockTime);

  const fill = content.fill || 'none';
  // Legacy: a CSS gradient string baked into `fill` (e.g. from older docs)
  // only works as an HTML background, so rect renders it via foreignObject.
  // The structured `content.gradient` (preferred) is handled below for all
  // shapes via an SVG <linearGradient>.
  const isCSSGradient = typeof fill === 'string' && fill.includes('gradient(');

  if (content.shape === 'rect' && isCSSGradient && !content.gradient) {
    return (
      <g
        className={`block-layer block-layer--shape ${animStyle.className}`}
        style={animStyle.style}
        data-layer-id={layer.id}
      >
        <foreignObject x={paintX} y={paintY} width={paintWidth} height={paintHeight}>
          <div
            style={{
              width: `${paintWidth}px`,
              height: `${paintHeight}px`,
              background: fill,
              borderRadius: content.borderRadius ? `${content.borderRadius}px` : undefined,
              pointerEvents: 'none',
            }}
          />
        </foreignObject>
      </g>
    );
  }

  const { fill: fillValue, def: fillDef } = resolveFill(
    defsId,
    fill,
    content.gradient,
    content.pattern,
  );
  const { filterAttr, def: filterDef } = resolveShapeFilter(defsId, content.filter);
  const dash = borderDashArray(content.borderStyle, content.strokeWidth);

  // Common style props for native SVG shapes. `line` is stroke-only.
  const shapeProps = {
    fill: fillValue,
    fillOpacity: content.fillOpacity,
    stroke: content.stroke,
    strokeWidth: content.strokeWidth,
    strokeDasharray: dash,
    ...(filterAttr ? { filter: filterAttr } : {}),
  };

  return (
    <g
      className={`block-layer block-layer--shape ${animStyle.className}`}
      style={animStyle.style}
      data-layer-id={layer.id}
    >
      {(fillDef || filterDef) && (
        <defs>
          {fillDef}
          {filterDef}
        </defs>
      )}
      {content.shape === 'rect' && (
        <rect
          x={paintX}
          y={paintY}
          width={paintWidth}
          height={paintHeight}
          rx={content.borderRadius}
          ry={content.borderRadius}
          {...shapeProps}
        />
      )}

      {content.shape === 'circle' && (
        <circle
          cx={x + width / 2}
          cy={y + height / 2}
          r={Math.min(width, height) / 2}
          {...shapeProps}
        />
      )}

      {content.shape === 'line' && (
        <line
          x1={x}
          y1={y}
          x2={x + width}
          y2={y + height}
          stroke={content.stroke || '#ffffff'}
          strokeWidth={content.strokeWidth || 2}
          strokeDasharray={dash}
        />
      )}
    </g>
  );
}

export default ShapeLayer;
